import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

type LabeledItem = { label: string; text: string };

function asLabeledItems(value: unknown): LabeledItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is LabeledItem => {
    return (
      !!item &&
      typeof item === 'object' &&
      typeof (item as LabeledItem).label === 'string' &&
      typeof (item as LabeledItem).text === 'string'
    );
  });
}

function RichText({ i18nKey }: { i18nKey: string }) {
  return (
    <Trans
      i18nKey={i18nKey}
      components={{
        0: <Link to="/privacy" className="text-[#1769ff] hover:underline" />,
      }}
    />
  );
}

function Paragraphs({
  items,
  i18nKeys,
}: {
  items?: string[];
  i18nKeys?: string[];
}) {
  if (i18nKeys && i18nKeys.length > 0) {
    return (
      <>
        {i18nKeys.map((key, i) => (
          <p key={key} className={i < i18nKeys.length - 1 ? 'mb-3' : undefined}>
            <RichText i18nKey={key} />
          </p>
        ))}
      </>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <>
      {items.map((text, i) => (
        <p key={i} className={i < items.length - 1 ? 'mb-3' : undefined}>
          {text}
        </p>
      ))}
    </>
  );
}

function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="list-disc pl-6 space-y-1 mt-2">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function LabeledBulletList({ items }: { items: LabeledItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="list-disc pl-6 space-y-1 mt-2">
      {items.map((item, i) => (
        <li key={i}>
          {item.label ? (
            <>
              <strong>{item.label}</strong> {item.text}
            </>
          ) : (
            item.text
          )}
        </li>
      ))}
    </ul>
  );
}

function TermsSection({ sectionKey }: { sectionKey: string }) {
  const { t } = useTranslation();
  const base = `terms.sections.${sectionKey}`;

  if (sectionKey === 'contact') {
    const email = t(`${base}.email`);
    const website = t(`${base}.website`);
    return (
      <section>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">{t(`${base}.title`)}</h2>
        <p className="mb-3">{t(`${base}.intro`)}</p>
        <p className="mb-3">
          <strong>{t(`${base}.emailLabel`)}</strong>{' '}
          <a href={`mailto:${email}`} className="text-[#1769ff] hover:underline">
            {email}
          </a>
        </p>
        <p>
          <strong>{t(`${base}.websiteLabel`)}</strong>{' '}
          <a href={website} className="text-[#1769ff] hover:underline" target="_blank" rel="noopener noreferrer">
            {website}
          </a>
        </p>
      </section>
    );
  }

  const paragraphs = asStringArray(t(`${base}.paragraphs`, { returnObjects: true }));
  const paragraphsAfter = asStringArray(t(`${base}.paragraphsAfter`, { returnObjects: true }));
  const items = asStringArray(t(`${base}.items`, { returnObjects: true }));
  const labeledItems = asLabeledItems(t(`${base}.labeledItems`, { returnObjects: true }));
  const listIntro = t(`${base}.listIntro`, { defaultValue: '' });
  const hasListIntro = listIntro && listIntro !== `${base}.listIntro`;

  const paragraphKeys = paragraphs.map((_, i) => `${base}.paragraphs.${i}`);
  const paragraphAfterKeys = paragraphsAfter.map((_, i) => `${base}.paragraphsAfter.${i}`);

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-3">{t(`${base}.title`)}</h2>
      {paragraphs.length > 0 && <Paragraphs i18nKeys={paragraphKeys} />}
      {hasListIntro && <p className={paragraphs.length > 0 ? 'mt-3 mb-2' : 'mb-2'}>{listIntro}</p>}
      <BulletList items={items} />
      <LabeledBulletList items={labeledItems} />
      {paragraphsAfter.length > 0 && (
        <div className={items.length > 0 || labeledItems.length > 0 || hasListIntro ? 'mt-3' : undefined}>
          <Paragraphs i18nKeys={paragraphAfterKeys} />
        </div>
      )}
    </section>
  );
}

export default function TermsPage() {
  const { t } = useTranslation();

  const intro = asStringArray(t('terms.intro', { returnObjects: true }));
  const introKeys = intro.map((_, i) => `terms.intro.${i}`);
  const sectionOrder = asStringArray(t('terms.sectionOrder', { returnObjects: true }));

  return (
    <div className="pt-24 pb-20">
      <SEO title={t('terms.title')} description={t('terms.seoDescription')} />
      <article className="max-w-3xl mx-auto px-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('terms.title')}</h1>
        <p className="text-gray-500 text-sm mb-12">
          {t('terms.lastUpdated')} {t('terms.lastUpdatedDate')}
        </p>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-600">
          <section>
            <Paragraphs i18nKeys={introKeys} />
          </section>

          {sectionOrder.map((key) => (
            <TermsSection key={key} sectionKey={key} />
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link to="/" className="text-[#1769ff] font-medium hover:underline">
            {t('terms.backToHome')}
          </Link>
        </div>
      </article>
    </div>
  );
}
